import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  ReferralPayoutStatus,
  ReferralStage,
  ReferredUserRow,
  ReplayReferralFlowResult,
  ReplayStepName,
  ReplayStepResult,
} from "./types";

const PAYOUT_STATUSES: ReferralPayoutStatus[] = [
  "pending",
  "succeeded",
  "failed",
];

function normalizePayoutStatus(value: unknown): ReferralPayoutStatus | null {
  if (typeof value !== "string") return null;
  return PAYOUT_STATUSES.includes(value as ReferralPayoutStatus)
    ? (value as ReferralPayoutStatus)
    : null;
}

const client = generateClient<Schema>();

export interface ListReferredUsersPage {
  items: ReferredUserRow[];
  nextToken: string | null;
}

export class ReferralsRepository {
  async listReferredUsers(opts: {
    nextToken?: string | null;
    limit?: number;
  }): Promise<ListReferredUsersPage> {
    const { data, errors } = await client.queries.listReferredUsers({
      input: {
        nextToken: opts.nextToken ?? null,
        limit: opts.limit ?? null,
      },
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) return { items: [], nextToken: null };

    const items: ReferredUserRow[] = (data.items ?? [])
      .filter((row): row is NonNullable<typeof row> => row !== null)
      .map((row) => ({
        userId: row.userId,
        email: row.email,
        referralCode: row.referralCode,
        referrerName: row.referrerName ?? null,
        businessId: row.businessId ?? null,
        businessName: row.businessName ?? null,
        referralStage: (row.referralStage as ReferralStage) ?? "not_referred",
        referralEnteredAt: row.referralEnteredAt ?? null,
        userCreatedAt: row.userCreatedAt ?? null,
        payoutStatus: normalizePayoutStatus(row.payoutStatus),
      }));

    return { items, nextToken: data.nextToken ?? null };
  }

  async replayReferralFlow(
    businessId: string,
  ): Promise<ReplayReferralFlowResult> {
    const { data, errors } = await client.mutations.backfillReferralSignup({
      input: { businessId },
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) throw new Error("Empty response from backfillReferralSignup");

    const steps: ReplayStepResult[] = (data.steps ?? [])
      .filter((s): s is NonNullable<typeof s> => s !== null)
      .map((s) => ({
        name: (s.name ?? "signup") as ReplayStepName,
        transitioned: !!s.transitioned,
        previousStage: (s.previousStage as ReferralStage | null) ?? null,
        newStage: (s.newStage as ReferralStage | null) ?? null,
        noOp: s.noOp ?? null,
        skippedReason: s.skippedReason ?? null,
        error: s.error ?? null,
        celloReport: s.celloReport
          ? {
              type: s.celloReport.type,
              success: !!s.celloReport.success,
              error: s.celloReport.error ?? null,
            }
          : null,
        payoutReport: s.payoutReport
          ? {
              status:
                (s.payoutReport.status as
                  "pending" | "succeeded" | "failed" | null) ?? null,
              lastError: s.payoutReport.lastError ?? null,
              attemptCount: s.payoutReport.attemptCount ?? 0,
            }
          : null,
      }));

    return {
      success: !!data.success,
      error: data.error ?? null,
      finalStage: (data.finalStage as ReferralStage | null) ?? null,
      steps,
    };
  }
}
